import classNames from 'classnames';
import React, { useContext, useEffect, useState } from 'react';
import { RefreshCcw } from '@styled-icons/feather';
import { isEqual } from 'lodash';
import { XCircleFill } from '@styled-icons/bootstrap/XCircleFill';
import styles from '../CollectionSettings.module.css';

import ButtonBlack from '../../../../Button/ButtonBlack';
import { ICollectionData, ICollectionDataItemMetadataKeys } from '../../../../../types';
import { BLOCK_ELEMENT_KEY, NOTION_KEYS } from '../../../types';
import { errToString } from '../../../../../utils/errors';
import { CollectionSettingsDataContext } from '../contextProvider';
import ToolbarDropdown from '../../SideToolbar/helpers/ToolbarDropdown';
import OptionsContainer from '../../../../Options';
import CheckboxItem from '../../../../Options/CheckboxItem';
import { DEFAULT_WIDTH_COLUMNS } from '../../SideToolbar/helpers/constants';
import Toggle from '../../../../Toggle';
import LoadingSmall from '../../../../../components/LoadingSmall';

function SearchNotion() {
  const [searchColumns, setsearchColumns] = useState(undefined);
  const [showSearch, setshowSearch] = useState(false);
  const [searchByOpen, setsearchByOpen] = useState(false);

  const {
    collectionData,
    element,
    seterr,
    updatePageCollectionData,
    columns,
    editorSyncData,
    loadingListColumns,
    listColumns,
    tables,
    tableConnected,
    err,
  } = useContext(CollectionSettingsDataContext);

  const oriSearchColumns = collectionData?.metadata?.[
    ICollectionDataItemMetadataKeys.SEARCH_COLUMNS];
  const oriShowSearch = collectionData?.metadata?.[ICollectionDataItemMetadataKeys.SHOW_SEARCH_BAR];

  const onCancelSearch = () => {
    if (collectionData) {
      const result = oriSearchColumns && oriSearchColumns?.length > 0
        ? oriSearchColumns?.reduce((map, obj) => ({ ...map, [obj]: true }), {})
        : {};
      setsearchColumns(result);
      setshowSearch(!!oriShowSearch);
    } else {
      setsearchColumns(undefined);
      setshowSearch(false);
    }
  };
  useEffect(() => {
    if (collectionData) {
      onCancelSearch();
    }
  }, [collectionData, oriSearchColumns, columns]);

  const listColumnNames = async () => {
    try {
      await listColumns(collectionData);
      onCancelSearch();
    } catch (e) {
      seterr(errToString(e));
    }
  };

  useEffect(() => {
    listColumnNames();
  }, [collectionData]);

  const updateSearch = async () => {
    const newCollectionData = { ...collectionData };
    const connectionDataID = element?.[BLOCK_ELEMENT_KEY.COLLECTION_DATA_ID];
    const newSearchColumns = Object.entries(searchColumns)?.map(
      ([key, value]) => (value ? key : undefined),
    ).filter((x) => x);
    newCollectionData.metadata = {
      ...newCollectionData.metadata,
      [ICollectionDataItemMetadataKeys.SEARCH_COLUMNS]:
      newSearchColumns?.length > 0 && showSearch ? newSearchColumns : undefined,
      [ICollectionDataItemMetadataKeys.SHOW_SEARCH_BAR]: showSearch,
    };
    const newConnData: ICollectionData = {
      [connectionDataID]: newCollectionData,
    };
    try {
      await updatePageCollectionData(newConnData);
      await editorSyncData(newCollectionData);
    } catch (e) {
      seterr(errToString(e));
    }
  };

  if (!tables
    || !tableConnected
    || !collectionData
    || !collectionData?.[NOTION_KEYS.TABLE_ID]) {
    return (
      <div className={classNames(styles.block, 'pb-6')}>
        <div className={classNames(styles.subtitleContainer, 'is-subtitle with-light-text-color mb-1')}>
          Please select a table first.
        </div>
      </div>
    );
  }

  return (
    <div className={classNames(styles.block, 'pb-6')}>
      <div className="is-flex is-flex-direction-row is-align-items-center is-justify-content-flex-start mb-5">
        <Toggle
          id={`show-searchbar-collection-${element?.id}`}
          active={showSearch}
          setactive={setshowSearch}
          label={(
            <p className={classNames({
              'with-accent-text-color': showSearch,
              'with-lighter-text-color': !showSearch,
            })}
            >
              Show search bar
            </p>
            )}
        />
      </div>

      <div className={classNames(styles.subtitleContainer, 'with-text-color has-text-weight-bold mb-1')}>
        Search by
      </div>

      <div className="is-flex is-align-items-center is-justify-content-flex-start">
        <ToolbarDropdown
          dropup={false}
          width={DEFAULT_WIDTH_COLUMNS}
          open={searchByOpen}
          setopen={setsearchByOpen}
          triggerClassname="m-0 p-0"
          contentClassname="m-0 pt-2"
          trigger={(
            <OptionsContainer
              selected={false}
              className="m-0 is-flex is-justify-content-space-between"
            >
              {
                  searchColumns && Object.values(searchColumns).filter((x) => x)?.length > 0
                    ? (
                      <>
                        <div className={styles.tagInput}>
                          {Object.entries(searchColumns)?.map(([k, v]) => v
                            && (
                            <div className="tags has-addons m-1">
                              <span className={classNames('tag', styles.searchTag)}>{k}</span>
                              <span
                                className={classNames('tag is-delete', styles.searchTag)}
                                role="none"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setsearchColumns((prevCols) => ({
                                    ...prevCols,
                                    [k]: false,
                                  }));
                                }}
                              />
                            </div>
                            ))}
                        </div>
                        <XCircleFill
                          size={17}
                          className="ml-2 with-lighter-text-color"
                          onClick={() => setsearchColumns({})}
                        />
                      </>
                    )
                    : (
                      <div
                        className={classNames(styles.tagInput, 'with-lighter-text-color')}
                      >
                        Select Columns
                      </div>
                    )
                }
            </OptionsContainer>
           )}
          content={(
            <>
              <div className="is-subtitle is-size-7 px-2">All Columns</div>
              <div style={{
                maxHeight: 300,
                overflowY: 'scroll',
                marginBottom: 20,
              }}
              >
                {Object.keys(columns)?.length > 0
                  ? Object.keys(columns)?.map((c) => (
                    <div className={styles.options}>
                      <CheckboxItem
                        title={(
                          <p className="with-text-color">
                            {c}
                          </p>
                        )}
                        selected={searchColumns?.[c]}
                        onClick={() => {
                          setsearchColumns((prevCols) => ({
                            ...prevCols,
                            [c]: !prevCols[c],
                          }));
                        }}
                        className={classNames(styles.checkboxItem)}
                        checkboxSize={13}
                      />
                    </div>
                  ))
                  : (
                    <div className={styles.options}>
                      <div className="is-subtitle">
                        You have not shared any tables with Typedream
                      </div>
                      <div className="with-accent-text-color">See image &rarr;</div>
                    </div>
                  )}
              </div>
            </>
            )}
        />
        <div
          className="with-accent-text-color with-cursor-pointer"
          role="none"
          onClick={listColumnNames}
        >
          <RefreshCcw size={20} className="ml-3" />
        </div>

        {
            loadingListColumns && (
              <LoadingSmall className="ml-1" loading={loadingListColumns} />
            )
          }
      </div>
      {
          !searchColumns || Object.values(searchColumns).filter((x) => x)?.length <= 0 ? (
            <div className="is-small-subtitle-size with-text-color mt-3">
              <b>Default</b>
              : Search by all columns
            </div>
          ) : null
        }
      <div className="is-flex is-flex-direction-row is-align-items-center mt-5">
        <ButtonBlack
          disabled={
                isEqual(searchColumns
                  ? Object.entries(searchColumns)?.map(
                    ([k, v]) => (v ? k : undefined),
                  ).filter((x) => x) : [], oriSearchColumns)
                && showSearch === !!oriShowSearch
              }
          onClick={updateSearch}
        >
          Save &rarr;
        </ButtonBlack>
        <div className="with-lighter-text-color with-cursor-pointer ml-3" role="none" onClick={onCancelSearch}>Cancel</div>
      </div>
      <div className="has-text-danger mt-2">{err}</div>
    </div>
  );
}

export default SearchNotion;
